import platform from 'platform';
import type { UserguideCardProps } from "./stories/Tools/UserguideContent";
import type { authTypes } from "./stories/Modal";

export function getKnownBrowserName() {
    const acceptedValues = ["chrome", "firefox", "edge", "safari", "opera", "microsoft edge"];
    if (platform.name) {
        const val = platform.name.trim();
        if (acceptedValues.includes(val.toLowerCase())) {
            return val;
        }
    }
    return "";
}

export function getKnownProductName() {
    //"BlackBerry", "Galaxy S4", "Lumia", "iPad", "iPod", "iPhone", "Kindle", "Kindle Fire", "Nexus", "Nook", "PlayBook", "TouchPad" and "Transformer"
    const acceptedValues = ["ipad", "iphone"];
    if (platform.product) {
       const product = platform.product.trim();
        if (acceptedValues.includes(product.toLowerCase())) {
            return product;
        }
    }
    return null;
}

function innerGetCurrentPlatform(acceptedValues : string[], valProducer: () => string) {
    if(platform.os && platform.os.family){
        const val = valProducer();
        if(acceptedValues.includes(val)){
            return val;
        }
    }
    return "other";
}


export function getCurrentPlatform () {
    return innerGetCurrentPlatform(["Windows", "OS X", "Android", "iOS"],() => platform.os?.family || "other");
}

export function isAndroidPlatform () {
    return getCurrentPlatform() === "Android";
}

export function getCurrentPlatformTrimmedLowercase () {
    return getCurrentPlatform().replace(/\s/g, "").toLowerCase();
}

export function capitalizeFirstLetter (str: string)  {
    if(!str){
        return "";
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function activateWindowLeaveModal () {
    window.onbeforeunload = function () {
        return "";
    };
}

export function deactivateWindowLeaveModal() {
    window.onbeforeunload = null;
}

export function openLinkSafely (url: string) {
    window.open(url, "_blank", "noopener, noreferrer");
}

export function openLinkSafelySameTarget (url: string) {
    deactivateWindowLeaveModal();
    window.open(url, "_self", "noopener, noreferrer");
}

export function configEntryToLinkInfo(entry:UserguideCardProps){
    const exp = new RegExp("{{(.*?)}}","g");
    const matches = entry.description.match(exp);

    if(!matches || matches.length === 0 || !entry.links){
        return undefined;
    }

    const info = [];
    let text = entry.description;
    for (const match of matches) {
        const innerMatch = match.substring(2,match.length-2);
        if(! (innerMatch in entry.links)){
            continue;
        }
        const arr = text.split(match);
        text = "";
        for(let i = 1;i<arr.length;i++){
            if(i !== 1){
                text = text + match;
            }
            text = text + arr[i];
        }
        info.push({
            text:arr[0],
            url: entry.links[innerMatch]
        });
    }
    info.push({text:text,url:null});
    return info;
}

export function getCurrentDeviceTypeByPlatform() : string {
    switch (getCurrentPlatformTrimmedLowercase()) {
        case "windows": {
            return "Windows Hello";
        }
        case "ios": {
            return getKnownProductName() || "iPhone";
        }
        case "osx": {
            const productName = getKnownProductName(); // this uses the user agent
            if (productName) {
                return productName;
            }

            // No user agent data for product or macbook
            const isPad = navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
            return isPad ? "iPad" : "Macbook";
        }
        case "android": {
            return "Android Device";
        }
        default: {
            return "Device";
        }
    }
}

export function identifyPlatformByDeviceName(name: string) : authTypes {
    // if user used our name suggestions, we can identify the platform
    if(!name){
        return "fido-passkey";
    }

    if(name.includes("iPhone") || name.includes("Macbook")) {
        return "apple";
    } else if(name.includes("Windows")) {
        return "windows_hello";
    } else if(name.includes("Android")) {
        return "android";
    } else if(name.includes("Security") || name.includes("Key")) {
        return "fido-passkey";
    } else {
        return "fido-passkey";
    }
}

function secureRandomCharUnbiased(chars: string): string {
    const max = Math.floor(256 / chars.length) * chars.length;

    let random = new Uint8Array(1);

    do {
        crypto.getRandomValues(random);
    } while (random[0] >= max);

    return chars.charAt(random[0] % chars.length);
}

export function makeRandomAlphaNumeric(length: number, characters?: string) {

    const chars = characters || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    let result = '';
    for (let i = 0; i < length; i++) {
        result += secureRandomCharUnbiased(chars);
    }
    return result;
}

export const apiPath = '/cp/api';
export const webappPath = '/cp/webapp';
export const contextPath = '/cp';
export const portalContextPath = "/cp/fidoCredentials";
export const portalProtectionPath = "/cp/protection";
export const portalLessSecureSignInPath = "/cp/less-secure-sign-ins";
export const getPortalUrl = () => window.origin + portalContextPath;
export const getProtectionUrl = () => window.origin + portalProtectionPath;
export const getLessSecureSignInUrl = () => window.origin + portalLessSecureSignInPath;